import React, {  useState } from "react";
import "../../../../../css/CashflowModeller2/DialogBoxes/DialogBoxes.css";
import Close from "../Buttons/Close";
import Input2Medium from "../Inputs/Input2Medium";
import AddButton from "../Buttons/AddButton";
import RemoveButton from "../Buttons/RemoveButton";
import InputDate from "../Inputs/InputDate";
import InputSmall from "../Inputs/InputSmall";
import { useCfmContext } from "../../Context/CfmContext";
import dayjs from "dayjs";
import TimelineOptions from "../../Utils/TimelineOptions";
import customParseFormat from "dayjs/plugin/customParseFormat";
import useDraggableDialog from "./Utils/UseDraggableDialog";
import Input1 from "../Inputs/Input1";
import Input1Big from "../Inputs/Input1Big";
dayjs.extend(customParseFormat);

const defaultRow = {
  name: "",
  dob: "",
  salary: { value: "", unit: TimelineOptions[1].value },
  currentExpenditure: { value: "", unit: TimelineOptions[1].value },
  retirementAge: "",
  retirementExpenditure: { value: "", unit: TimelineOptions[1].value },
};

/**
 * PersonalDetailsDialogBox component renders a draggable dialog box for managing personal details,
 * including name, date of birth, salary, current and retirement expenditures, and retirement age.
 * Allows adding, editing, and removing personal detail rows with validation for age and retirement age.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.onClose - Callback function to close the dialog box.
 *
 * @returns {JSX.Element} The rendered dialog box component for personal details.
 *
 * @example
 * <PersonalDetailsDialogBox onClose={() => setShowDialog(false)} />
 *
 * @remarks
 * - Uses context from useCfmContext for state management.
 * - Validates that age and retirement age are at least 18.
 * - Supports a maximum of two personal detail entries.
 * - Allows inflation value synchronization across all rows (commented out in current code).
 */
const PersonalDetailsDialogBox = ({ onClose }) => {
  const { state, dispatch } = useCfmContext();
  const details = state.details || [];
  const [newRow, setNewRow] = useState(defaultRow);
  const [errors, setErrors] = useState({});
  const { ref, position, handleMouseDown } = useDraggableDialog();

  /**
   * Calculates the age in years based on the provided date of birth.
   *
   * @param {string} dob - The date of birth in "DD/MM/YYYY" format.
   * @returns {number} The calculated age in years. Returns 0 if the input is invalid or not provided.
   */
  const getAge = (dob) => {
    if (!dob || !dayjs(dob, "DD/MM/YYYY").isValid()) return 0;
    return dayjs().diff(dayjs(dob, "DD/MM/YYYY"), "year");
  };

  /**
   * Validates a row object to ensure all required personal details fields are filled.
   *
   * Checks if the row exists and verifies that the following fields are non-empty after trimming:
   * - name
   * - dob (date of birth)
   * - salary.value
   * - currentExpenditure.value
   * - retirementAge
   * - retirementExpenditure.value
   *
   * Note: This function currently only checks for filled fields and does not validate age or error states.
   *
   * @param {Object} row - The row object containing personal details to validate.
   * @param {string} row.name - The person's name.
   * @param {string} row.dob - The person's date of birth.
   * @param {Object} row.salary - The salary object.
   * @param {string} row.salary.value - The salary value as a string.
   * @param {Object} row.currentExpenditure - The current expenditure object.
   * @param {string} row.currentExpenditure.value - The current expenditure value as a string.
   * @param {string} row.retirementAge - The retirement age as a string.
   * @param {Object} row.retirementExpenditure - The retirement expenditure object.
   * @param {string} row.retirementExpenditure.value - The retirement expenditure value as a string.
   * @returns {boolean} Returns true if all required fields are filled, otherwise false.
   */
  const isRowValid = (row) => {
    if (!row) return false;

    const age = getAge(row.dob);
    const retirement = Number(row.retirementAge);

    const allFieldsFilled = [
      row.name.trim(),
      row.dob.trim(),
      row.salary.value.trim(),
      row.currentExpenditure.value.trim(),
      row.retirementAge.trim(),
      row.retirementExpenditure.value.trim(),
    ].every(Boolean);

    const noErrors = !errors.dob && !errors.retirementAge;

    // return allFieldsFilled && noErrors && age >= 18 && retirement >= 18;
    return allFieldsFilled;
  };

  /**
   * Handles the addition of a new personal detail row.
   * 
   * Validates the input fields for completeness and correctness:
   * - Checks if all required fields are filled.
   * - Validates date of birth (must be a valid date and age >= 18).
   * - Validates retirement age (must be >= 18 and not less than current age).
   * 
   * If validation fails, sets error states and aborts.
   * If validation passes, updates the details state with the new row,
   * resets the input row and clears errors.
   * 
   * @function
   * @returns {void}
   */
  const handleAdd = () => {
    const age = getAge(newRow.dob);
    const retirement = Number(newRow.retirementAge);

    const newErrors = {
      dob: !newRow.dob || !dayjs(newRow.dob, "DD/MM/YYYY").isValid(),
      // !newRow.dob || age < 18 || !dayjs(newRow.dob, "DD/MM/YYYY").isValid(),
      retirementAge: !newRow.retirementAge || retirement < 18,
      retirementAgesmall: retirement < age,
    };

    const missingFields = [
      newRow.name.trim(),
      newRow.dob.trim(),
      newRow.salary.value.trim(),
      newRow.currentExpenditure.value.trim(),
      newRow.retirementAge.trim(),
      newRow.retirementExpenditure.value.trim(),
    ].some((v) => !v);

    if (
      missingFields ||
      newErrors.dob ||
      newErrors.retirementAge ||
      newErrors.retirementAgesmall
    ) {
      setErrors(newErrors);
      return;
    }

    // All good, add:
    const updatedDetails = [...(state.details || []), newRow];
    dispatch({ type: "UPDATE_DETAILS", payload: updatedDetails });
    setNewRow(defaultRow);
    setErrors({});
  };

  /**
   * Removes an item from the details array at the specified index and updates the state.
   *
   * @function
   * @param {number} index - The index of the item to remove from the details array.
   * @returns {void}
   */
  const handleRemove = (index) => {
    const updated = state.details.filter((_, i) => i !== index);
    dispatch({ type: "UPDATE_DETAILS", payload: updated });
  };

  /**
   * Updates a specific field in a row of the details array and dispatches the updated array.
   *
   * @param {number} index - The index of the row to update in the details array.
   * @param {string} field - The field to update. Can be a nested field using dot notation (e.g., "parent.child").
   * @param {*} value - The new value to set for the specified field.
   *
   * @returns {void}
   */
  const updateRow = (index, field, value) => {
    const updated = [...details];
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      updated[index][parent][child] = value;
    } else {
      updated[index][field] = value;
    }

    dispatch({ type: "UPDATE_DETAILS", payload: updated });
  };

  // useEffect(() => {
  //   console.log(state.details);
  // }, [state.details]);

  return (
    <div
      ref={ref}
      className="dialog-box"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <div
        className="dialog-header"
        onMouseDown={handleMouseDown}
        style={{ cursor: "grab" }}
      >
        <div className="Title">Personal Details</div>
        <div className="dialog-header-buttons">
          <div className="Content-Small" onClick={onClose}>
            Close
          </div>
          <div onClick={onClose}>
            <Close />
          </div>
        </div>
      </div>

      {details.length < 2 && (
        <div className="personaldetails-dialogbox-inputrow">
          <div className="personaldetails-dialogbox-inputColumn">
            <div className="Title">Current</div>
            <div className="dialog-row-inputs">
              <Input1Big
                label="Name"
                placeholderText="William"
                type="text"
                value={newRow.name}
                onChange={(val) => {
                  setNewRow({ ...newRow, name: val });
                  setErrors((prev) => ({
                    ...prev,
                    name: false,
                  }));
                }}
              />
              <InputDate
                label="DOB"
                value={newRow.dob}
                isError={errors.dob || errors.retirementAgesmall}
                onChange={(val) => {
                  setNewRow({ ...newRow, dob: val });
                  setErrors((prev) => ({
                    ...prev,
                    dob: false,
                    retirementAgesmall: false,
                  }));

                  // const age = getAge(val);
                  // setErrors((prev) => ({
                  //   ...prev,
                  //   dob: age < 18,
                  // }));
                }}
              />

              <Input1
                label="Salary"
                type="number"
                value={newRow.salary?.value || ""}
                onChange={(val) => {
                  setNewRow({
                    ...newRow,
                    salary: {
                      ...newRow.salary,
                      value: val,
                      unit: TimelineOptions[1].value,
                    },
                  });

                  setErrors((prev) => ({
                    ...prev,
                    salary: false,
                  }));
                }}
                currency={true}
              />
              <Input2Medium
                label="Expenditure"
                placeholderText="£25,000"
                type="number"
                value={newRow.currentExpenditure?.value}
                options={TimelineOptions}
                onChange={(val) => {
                  setNewRow({
                    ...newRow,
                    currentExpenditure: {
                      ...newRow.currentExpenditure,
                      value: val,
                    },
                  });
                  setErrors((prev) => ({
                    ...prev,
                    currentExpenditure: false,
                  }));
                }}
                onUnitChange={(unit) =>
                  setNewRow({
                    ...newRow,
                    currentExpenditure: {
                      ...newRow.currentExpenditure,
                      unit: unit,
                    },
                  })
                }
                currentUnit={newRow.currentExpenditure?.unit}
              />
              {/* <InputMedium
                label="Inflation"
                type="number"
                value={state.inflation}
                onChange={(val) => syncInflationAcrossRows(val)}
                per={true}
              /> */}
            </div>
          </div>

          <div className="personaldetails-dialogbox-inputColumn">
            <div className="Title">Retirement</div>
            <div className="dialog-row-inputs">
              <InputSmall
                label="Age"
                isError={errors.retirementAge || errors.retirementAgesmall}
                type="number"
                value={newRow.retirementAge}
                onChange={(val) => {
                  setNewRow({ ...newRow, retirementAge: val });

                  // const isTooYoung = Number(val) < 18;
                  setErrors((prev) => ({
                    ...prev,
                    retirementAge: false,
                    retirementAgesmall: false,
                  }));
                }}
              />

              <Input2Medium
                label="Expenditure"
                placeholderText="£20,000"
                type="number"
                value={newRow.retirementExpenditure.value}
                options={TimelineOptions}
                onChange={(val) => {
                  setNewRow({
                    ...newRow,
                    retirementExpenditure: {
                      ...newRow.retirementExpenditure,
                      value: val,
                    },
                  });
                  setErrors((prev) => ({
                    ...prev,
                    retirementExpenditure: false,
                  }));
                }}
                onUnitChange={(unit) =>
                  setNewRow({
                    ...newRow,
                    retirementExpenditure: {
                      ...newRow.retirementExpenditure,
                      unit: unit,
                    },
                  })
                }
                currentUnit={newRow.retirementExpenditure?.unit}
              />
              <AddButton isDisabled={!isRowValid(newRow)} onClick={handleAdd} />
            </div>
            {errors.dob && (
              <div className="error Label">Invalid date of birth.</div>
            )}
            {errors.retirementAge && (
              <div className="error Label">
                Retirement age must be 18 or older.
              </div>
            )}
            {errors.retirementAgesmall && !errors.retirementAge && (
              <div className="error Label">Invalid age range.</div>
            )}
          </div>
        </div>
      )}

      {details.map((row, index) => {
        const showLabels = index === 0 && details.length === 2;
        return (
          <React.Fragment key={index}>
            <div className="no-dovider-dialogBox" />
            <div className="personaldetails-dialogbox-inputrow">
              <div className="personaldetails-dialogbox-inputColumn">
                {showLabels && <div className="Title">Current</div>}
                <div className="dialog-row-inputs">
                  <Input1Big
                    label={showLabels ? "Name" : undefined}
                    value={row.name}
                    readOnly={true}
                    // onChange={(val) => {
                    //   const updated = [...localState];
                    //   updated[index].name = val;
                    //   setLocalState(updated);
                    //   if (val.trim() !== "") {
                    //     updateContextField(index, "name", val);
                    //   }
                    // }}
                  />
                  <InputDate
                    label={showLabels ? "DOB" : undefined}
                    value={row.dob}
                    readOnly
                  />
                  <Input1
                    label={showLabels ? "Salary" : undefined}
                    type="number"
                    value={row.salary?.value || ""}
                    onChange={(val) => updateRow(index, "salary.value", val)}
                    currency={true}
                  />

                  <Input2Medium
                    label={showLabels ? "Expenditure" : undefined}
                    value={row.currentExpenditure?.value || ""}
                    options={TimelineOptions}
                    onChange={(val) =>
                      updateRow(index, "currentExpenditure.value", val)
                    }
                    onUnitChange={(unit) =>
                      updateRow(index, "currentExpenditure.unit", unit)
                    }
                    currentUnit={row.currentExpenditure?.unit}
                  />

                  {/* {showLabels && (
                    <InputMedium
                      per={true}
                      label="Inflation"
                      type="number"
                      value={state.inflation}
                      onChange={(val) => syncInflationAcrossRows(val)}
                    />
                  )} */}
                </div>
              </div>

              <div className="personaldetails-dialogbox-inputColumn">
                {showLabels && <div className="Title">Retirement</div>}
                <div className="dialog-row-inputs">
                  <InputSmall
                    label={showLabels ? "Age" : undefined}
                    type="number"
                    value={row.retirementAge}
                    onChange={(val) => updateRow(index, "retirementAge", val)}
                  />

                  <Input2Medium
                    label={showLabels ? "Expenditure" : undefined}
                    value={row.retirementExpenditure?.value || ""}
                    options={TimelineOptions}
                    onChange={(val) =>
                      updateRow(index, "retirementExpenditure.value", val)
                    }
                    onUnitChange={(unit) =>
                      updateRow(index, "retirementExpenditure.unit", unit)
                    }
                    currentUnit={row.retirementExpenditure?.unit}
                  />

                  <RemoveButton onClick={() => handleRemove(index)} />
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default PersonalDetailsDialogBox;
